import { useCallback, useEffect, useSyncExternalStore } from "react";
import { api, type Project } from "../../shared/api.js";
import { onForget } from "../../shared/keep.js";
import { watchProject } from "../../shared/live.js";

/**
 * Les projets ouverts, un seul exemplaire par identifiant. Chaque écran lisait le sien : l'espace du projet (App), son
 * écran (Project, Ready) et ses rushes (Footage) demandaient le même projet, et deux écrans le suivaient chacun toutes les
 * 3 s. Ici : une lecture partagée, un seul suivi en direct (compté), et un projet relu à l'identique (même JSON) ne
 * réveille personne — l'éditeur ne se redessine plus à chaque relecture. La liste de l'accueil le donne d'avance :
 * ouvrir une carte montre le projet tout de suite.
 *
 * Un projet gardé peut avoir vieilli (un brouillon lancé depuis) : il s'affiche en attendant, mais un écran ne prend une
 * décision qui l'emmène ailleurs (vers la création, vers le montage) que sur un projet relu depuis qu'il le suit
 * (`verified`). Sinon, le brouillon gardé par les rushes renvoyait vers la création un montage qu'on venait de lancer.
 */
type Entry = {
  project: Project | null;
  /** Le projet tel que reçu, pour savoir s'il a vraiment changé. */
  json: string;
  error: unknown;
  listeners: Set<() => void>;
  watchers: number;
  stop: (() => void) | null;
  reading: Promise<Project> | null;
  /** Le suivi en cours (un de plus à chaque reprise) et celui pendant lequel le projet a été reçu pour la dernière fois. */
  session: number;
  seen: number;
};
const entries = new Map<string, Entry>();

function entry(id: string): Entry {
  let e = entries.get(id);
  if (!e) {
    e = { project: null, json: "", error: null, listeners: new Set(), watchers: 0, stop: null, reading: null, session: 0, seen: -1 };
    entries.set(id, e);
  }
  return e;
}
const notify = (e: Entry) => [...e.listeners].forEach((fn) => fn());

export const projectStore = {
  get: (id: string): Project | null => entries.get(id)?.project ?? null,
  error: (id: string): unknown => entries.get(id)?.error ?? null,
  /** Le projet a été relu depuis que l'écran le suit : on peut décider d'après lui (rediriger, ouvrir l'éditeur). */
  verified: (id: string): boolean => {
    const e = entries.get(id);
    return !!e && e.watchers > 0 && e.seen === e.session;
  },
  subscribe(id: string, fn: () => void): () => void {
    const e = entry(id);
    e.listeners.add(fn);
    return () => void e.listeners.delete(fn);
  },
  /** Pose un projet (lu, renommé, suivi en direct) : ceux qui le lisent ne sont prévenus que s'il a changé. */
  put(p: Project) {
    const e = entry(p.id);
    const json = JSON.stringify(p);
    const seen = e.watchers > 0 ? e.session : e.seen;
    if (json === e.json && e.error === null && seen === e.seen) return;
    e.json = json;
    e.project = p;
    e.error = null;
    e.seen = seen;
    notify(e);
  },
  /** Une lecture ou un suivi qui échoue : l'écran le dit s'il n'a rien d'autre à montrer. */
  fail(id: string, error: unknown) {
    const e = entry(id);
    e.error = error;
    notify(e);
  },
  /**
   * Une liste fraîche de l'accueil : chaque projet d'avance, sans remplacer un projet suivi plus récent. Jamais une
   * liste gardée d'une visite passée (un brouillon depuis lancé renverrait son écran vers la création).
   */
  seed(list: Project[]) {
    for (const p of list) {
      const cur = entries.get(p.id)?.project;
      if (cur && Date.parse(cur.updatedAt) > Date.parse(p.updatedAt)) continue;
      projectStore.put(p);
    }
  },
  /** Une lecture par l'API ; deux écrans qui la demandent en même temps partagent la même réponse. */
  read(id: string): Promise<Project> {
    const e = entry(id);
    if (e.reading) return e.reading;
    const reading = api.project(id).then(
      (p) => {
        if (e.reading === reading) e.reading = null;
        projectStore.put(p);
        return p;
      },
      (error: unknown) => {
        if (e.reading === reading) e.reading = null;
        throw error;
      },
    );
    e.reading = reading;
    return reading;
  },
  /** Le suivi en direct (Firestore, sinon toutes les 3 s), compté : le dernier écran qui quitte le projet l'arrête. */
  watch(id: string): () => void {
    const e = entry(id);
    if (e.watchers++ === 0) {
      // Une nouvelle reprise : ce qui est gardé s'affiche, mais n'est « vérifié » qu'à la prochaine réponse.
      e.session++;
      // Un échec d'une visite passée ne s'affiche plus : le suivi qui commence dira le sien.
      if (e.error !== null) {
        e.error = null;
        notify(e);
      }
      e.stop = watchProject(
        id,
        (p) => projectStore.put(p),
        (error) => projectStore.fail(id, error),
        3000,
        () => projectStore.read(id),
      );
    }
    return () => {
      if (--e.watchers > 0) return;
      e.stop?.();
      e.stop = null;
    };
  },
};

/** Un autre compte : ses projets ne se montrent pas sous le précédent (les écrans ouverts gardent leur suivi). */
onForget((_stale, accountChanged) => {
  if (!accountChanged) return;
  for (const e of entries.values()) {
    e.project = null;
    e.json = "";
    e.error = null;
    notify(e);
  }
});

/**
 * Le projet `id`, à jour : suivi en direct tant que l'écran est ouvert (`live`), sinon lu une fois s'il n'est pas
 * déjà connu. Le même objet d'un rendu à l'autre tant qu'il ne change pas.
 */
export function useProject(
  id: string,
  { live = true }: { live?: boolean } = {},
): { project: Project | null; error: unknown; verified: boolean } {
  const subscribe = useCallback((fn: () => void) => projectStore.subscribe(id, fn), [id]);
  const project = useSyncExternalStore(subscribe, () => projectStore.get(id));
  const error = useSyncExternalStore(subscribe, () => projectStore.error(id));
  const verified = useSyncExternalStore(subscribe, () => projectStore.verified(id));
  useEffect(() => {
    if (live) return projectStore.watch(id);
    if (!projectStore.get(id)) projectStore.read(id).catch((e: unknown) => projectStore.fail(id, e));
  }, [id, live]);
  return { project, error, verified };
}
